import React, { useCallback, useState } from 'react';

import { VisuallyHidden } from '@rbilabs/components-library';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

import { IBaseItem, ICartEntry } from '@rbi-ctg/menu';
import { useMenuContext } from 'state/menu';
import { useOrderContext } from 'state/order';
import { createCartEntry } from 'utils/cart';
import { routes } from 'utils/routing';

import SectionHeading from '../section-heading';

import { UpsellContainer } from './styles';
import { IUpsellProps } from './types';
import UpsellItem from './upsell-item';
import { UpsellSkeleton } from './upsell.skeleton';

const UpsellItems = styled.div`
  overflow-x: auto;
  scroll-snap-type: x mandatory;
  display: flex;
  flex-direction: row;
`;

export const UpsellLoadingContainer = styled.div`
  min-height: 100px;
  display: flex;
`;

export const Upsell: React.FC<IUpsellProps> = ({
  hasBorderAtCheckout,
  items,
  loading,
  recommender,
  recommendationToken,
}) => {
  const { pricingFunction } = useMenuContext();
  const { addToCart } = useOrderContext();
  const { formatMessage } = useIntl();

  const [orderAddedAlertMessage, setOrderAddedAlertMessage] = useState('');

  const handleAddToCart = useCallback(
    (item: IBaseItem) => {
      const price = pricingFunction(item);
      const entry: ICartEntry = {
        ...createCartEntry({
          item,
          price,
          location: { pathname: `${routes.menu}/${item._type}-${item._id}` },
        }),
        isUpsell: true,
        recommendationToken,
        recommender,
      };

      addToCart(entry);

      setOrderAddedAlertMessage(
        formatMessage({ id: 'itemAddedToCart' }, { itemName: item.name?.locale })
      );
    },
    [addToCart, formatMessage, pricingFunction, recommendationToken, recommender]
  );

  if (!items.length && !loading) {
    return null;
  }

  return (
    <UpsellContainer data-testid="upsell" hasBorderAtCheckout={hasBorderAtCheckout}>
      <SectionHeading id="add-to-order-header">
        {formatMessage({ id: 'addToOrderHeader' })}
      </SectionHeading>
      {loading && !items.length ? (
        <UpsellLoadingContainer>
          <UpsellSkeleton hasBorderAtCheckout={hasBorderAtCheckout} />
          <UpsellSkeleton hasBorderAtCheckout={hasBorderAtCheckout} />
        </UpsellLoadingContainer>
      ) : (
        <UpsellItems aria-labelledby="add-to-order-header">
          {items.map(item => (
            <UpsellItem key={item._id} item={item} addToCard={handleAddToCart} />
          ))}
        </UpsellItems>
      )}
      {orderAddedAlertMessage && (
        <VisuallyHidden role="alert">{orderAddedAlertMessage}</VisuallyHidden>
      )}
    </UpsellContainer>
  );
};
